
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Shield, AlertTriangle, CheckCircle, Loader2 } from "lucide-react";

interface ModerationResult {
  total_profiles_checked: number;
  total_photos_checked: number;
  inappropriate_photos_found: number;
  photos_removed: number;
  results: Array<{
    user_id: string;
    original_count: number;
    final_count: number;
    removed_count: number;
  }>;
}

const RetroactiveModerationAdmin = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<ModerationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runRetroactiveModeration = async () => {
    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      toast({
        title: "Starting retroactive moderation",
        description: "This may take several minutes depending on the number of photos...",
      });

      const { data, error } = await supabase.functions.invoke('retroactive-content-moderation');

      if (error) {
        throw error;
      }

      setResult(data);
      
      toast({
        title: "Moderation complete",
        description: `Checked ${data.total_photos_checked} photos, removed ${data.photos_removed} inappropriate images.`,
      });

    } catch (error: any) {
      console.error('Retroactive moderation error:', error);
      setError(error.message || 'Failed to run retroactive moderation');
      
      toast({
        title: "Moderation failed",
        description: error.message || 'An error occurred during retroactive moderation',
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            Retroactive Content Moderation
          </CardTitle>
          <CardDescription>
            Scan all existing profile photos and remove any content that violates community guidelines.
            This process uses Google Vision AI to detect inappropriate content.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={runRetroactiveModeration}
              disabled={isRunning}
              variant="outline"
              className="flex items-center gap-2"
            >
              {isRunning ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
              {isRunning ? "Running Moderation..." : "Start Retroactive Scan"}
            </Button>
            
            {isRunning && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Processing...
              </Badge>
            )}
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">Error</span>
              </div>
              <p className="text-red-700 mt-1">{error}</p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Moderation Complete</span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-2xl font-bold text-blue-900">{result.total_profiles_checked}</div>
                  <div className="text-sm text-blue-700">Profiles Checked</div>
                </div>
                
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{result.total_photos_checked}</div>
                  <div className="text-sm text-gray-700">Photos Scanned</div>
                </div>
                
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="text-2xl font-bold text-orange-900">{result.inappropriate_photos_found}</div>
                  <div className="text-sm text-orange-700">Inappropriate Found</div>
                </div>
                
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="text-2xl font-bold text-red-900">{result.photos_removed}</div>
                  <div className="text-sm text-red-700">Photos Removed</div>
                </div>
              </div>

              {result.results.filter(r => r.removed_count > 0).length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Profiles with Removed Content:</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {result.results
                      .filter(r => r.removed_count > 0)
                      .map((userResult, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm font-mono">{userResult.user_id.slice(0, 8)}...</span>
                          <Badge variant="destructive" className="text-xs">
                            -{userResult.removed_count} photos
                          </Badge>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RetroactiveModerationAdmin;
